import { ENVIRONMENT } from '@updater-core/lib/environment';
import { track, ERROR } from '@updater-core/lib/tracker';
import { createStore } from '@updater-core/lib/store';
import { client } from '../../apps/angularjs/helpers/content';
import { maintenance } from '../../apps/angularjs/state/contentful/entries.json';

// Grab the state of the maintenance configuration from Contentful
const checkMaintenanceState = client
  ? () =>
      Promise.race([
        client.getEntry(maintenance).then(({ fields } = {}) => fields),
        new Promise((resolve) => setTimeout(() => resolve({}), 3000)),
      ])
  : () => Promise.resolve({});

// Re-check the maintenance state on any failed (not 200-299) response from our
// api. If we’re now in maintenance mode, render the maintenance page
const handleResponse = async ({ setState, url = '', ok }) => {
  try {
    const isApiRequest = url.includes(ENVIRONMENT.endpoints.api.url);

    if (isApiRequest && !ok) {
      const state = await checkMaintenanceState();

      setState(state);
    }
  } catch (e) {
    track({
      domain: 'mover',
      object: 'maintenance_check',
      verb: ERROR,
      details: {
        error: e,
      },
    });
  }
};

export const buildMaintenance = () => {
  const store = createStore({});

  // Check the maintenance state on any failed requests to our API by
  // overriding fetch calls
  const browserFetch = window.fetch;
  if (browserFetch) {
    window.fetch = async (...args) => {
      const response = (await browserFetch(...args)) || {};

      handleResponse({
        setState: store.setState,
        url: response.url,
        ok: response.ok,
      });

      return response;
    };
  }

  // Check the maintenance state on any failed requests to our API by
  // overriding XHR requests—used by older code such as angularjs/redux
  const oldXHROpen = window.XMLHttpRequest?.prototype?.open;

  function newXHROpen(...args) {
    this.addEventListener('load', async () => {
      const [, url] = args || [];
      const ok = Boolean(this.status?.toString()?.match(/2\d\d/));

      handleResponse({ setState: store.setState, url, ok });
    });

    return oldXHROpen.apply(this, args);
  }

  if (oldXHROpen) {
    window.XMLHttpRequest.prototype.open = newXHROpen;
  }

  checkMaintenanceState().then((state) => {
    store.setState(state);
  });

  return {
    getState: store.getState,
    actions: {
      checkMaintenanceState,
    },
    connect: (fn) => {
      fn(store.getState());
      return store.subscribe(fn);
    },
    selectors: {
      checked: (state) => Boolean(state.showMaintenance),
      underMaintenance: (state) => state.showMaintenance?.[ENVIRONMENT.name],
    },
  };
};
